import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import PictionJS from 'piction-js';
import cn from 'classnames';
import {
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import {
  showCropTool,
  showImageAnnotatorTool,
  showAudioAnnotatorTool,
  showVideoAnnotatorTool,
  showVideoTrimmerTool,
  showMetaEditTool,
} from '../../utils/uiUtils';
import { isEditMode } from '../../utils/piction';
import { UMO_TYPE_IMAGE, UMO_TYPE_VIDEO, UMO_TYPE_AUDIO } from '../../constants/umo';
import appConfig from '../../appConfig';
import UMODownloadView from '../UMODownloadView';
import { showUMOModifyDialog } from '../../actions/mainUI';
import {
  UMO_MODIFY_TYPE_RESIZE,
  UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE,
  UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR,
} from '../../constants/umoModifications';
import { populateMetadata, showMetaCopy } from '../../actions/metaAttach';
import UMOGridDropDownSideMenu from './UMOGridDropDownSideMenu';
import EditIcon from '../Icons/EditDefaul';
import CopyIcon from '../Icons/CopyDefault';
import MediaPopulateIcon from '../Icons/MediaPopulate';
import ResizeIcon from '../Icons/ResizeAllDefault';
import PhotographyRequestIcon from '../Icons/PhotographyRequestDefault';
import CropIcon from '../Icons/Crop';
import RelateIcon from '../Icons/RelateAllItemsDefault';
import ViewPeersIcon from '../Icons/ViewPeers';
import DownloadIcon from '../Icons/Dowload';
import ImageIcon from '../Icons/Image';
import ToolsIcon from '../Icons/Tool';

import FlipHorizontal from '../Icons/FlipHorizontal';
import RotateIcon from '../Icons/RotateIcon';

const KEY_CROP = 'KEY_CROP';
const KEY_ROTATE = 'KEY_ROTATE';
const KEY_MIRROR = 'KEY_MIRROR';
const KEY_IMAGE_ANNOTATE = 'KEY_IMAGE_ANNOTATE';
const KEY_RESIZE = 'KEY_RESIZE';
const KEY_AUDIO_ANNOTATE = 'KEY_AUDIO_ANNOTATE';
const KEY_VIDEO_ANNOTATE = 'KEY_VIDEO_ANNOTATE';
const KEY_TRIMMER = 'KEY_TRIMMER';
const KEY_META_EDIT = 'KEY_META_EDIT';
const KEY_VIEW_RELATED = 'KEY_VIEW_RELATED';
const KEY_META_POPULATE = 'KEY_META_POPULATE';
const KEY_META_COPY = 'KEY_META_COPY';

const mapDispatchToProps = (dispatch) => ({
  dispatch: (actionToDispatch) => dispatch(actionToDispatch),
});

function UMOGridItemOverlayMenu({
  umo,
  retrieveRelatedUMOs,
  intl,
  dispatch,
  classModifier,
  isRestricted,
}) {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);
  const itemClass = 'umo-grid-item__overlay-more-images-option';
  const origFile = umo.originalList.all.find((item) => item.index === '1');
  // if filetypes are changed here- change also in image-cropper
  const isUnsupportedFileType =
    origFile && /.*\.(DNG|CR2|NEF|RAW)$/i.test(origFile.url);
  const hasNoDerivativeAccess =
    !umo.originalList || !umo.originalList.all || umo.originalList.all.length === 0;
  const isUnsupportedForEdit = isUnsupportedFileType || hasNoDerivativeAccess;

  const onMenuItemClick = (e) => {
    const type = e.target.dataset.keytype;
    switch (type) {
      case KEY_CROP:
        showCropTool(umo, 'CROP');
        break;
      case KEY_ROTATE:
        showCropTool(umo, 'ROTATE');
        break;
      case KEY_MIRROR:
        //showCropTool(umo, 'ADJUST');
        dispatch(
          showUMOModifyDialog({
            modType: UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR,
            umoId: umo.id,
            umoUrl: umo.webQuality.url,
          })
        );
        break;
      case KEY_IMAGE_ANNOTATE:
        showImageAnnotatorTool(umo, dispatch);
        break;
      case KEY_RESIZE:
        dispatch(
          showUMOModifyDialog(
            {
              modType: UMO_MODIFY_TYPE_RESIZE,
              selectAllOverride: true,
              origWidth: umo.data.o.find( o => o.$.pn.toLowerCase().includes('original'))?.$.w,
            },
            [umo.id],
            null,
            null
          )
        );
        break;
      case KEY_AUDIO_ANNOTATE:
        showAudioAnnotatorTool(umo, dispatch);
        break;
      case KEY_VIDEO_ANNOTATE:
        showVideoAnnotatorTool(umo, dispatch);
        break;
      case KEY_TRIMMER:
        showVideoTrimmerTool(umo, dispatch);
        break;
      case KEY_META_EDIT:
        showMetaEditTool(umo, dispatch);
        break;
      case KEY_VIEW_RELATED:
        retrieveRelatedUMOs(umo.id);
        break;
      case KEY_META_POPULATE:
        dispatch(populateMetadata(umo));
        break;
      case KEY_META_COPY:
        dispatch(showMetaCopy(umo));
        break;
      default:
        break;
    }
    e.stopPropagation();
  };

  const toolOptions = [];
  let showRelateMenu = false;
  let relateOption = '';
  const isUMOMenu = appConfig.menuOptions && appConfig.menuOptions.umoMenu;

  if (isUMOMenu && appConfig.menuOptions.umoMenu.length > 0) {
    const menuOptions = appConfig.menuOptions.umoMenu;
    menuOptions.forEach((value) => {
      switch (value) {
        case 'editMetadata':
          toolOptions.push({
            key: KEY_META_EDIT,
            label: intl.formatMessage({ id: 'umoGridItem.menuEditMetaData' }),
            icon: <EditIcon style={{ marginRight: 5 }} />,
          });
          break;
        case 'crop':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_CROP,
              label: intl.formatMessage({ id: 'umoGridItem.menuCrop' }),
              disabled: isUnsupportedForEdit,
              tooltipMessage: hasNoDerivativeAccess
                ? intl.formatMessage({ id: 'umoGridItem.noDerivativeAccess' })
                : intl.formatMessage({ id: 'umoGridItem.cropUnsupportedTypes' }),
              icon: <CropIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        case 'rotate':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_ROTATE,
              label: intl.formatMessage({ id: 'umoGridItem.menuRotate' }),
              disabled: isUnsupportedForEdit,
              tooltipMessage: hasNoDerivativeAccess
                ? intl.formatMessage({ id: 'umoGridItem.noDerivativeAccess' })
                : intl.formatMessage({ id: 'umoGridItem.rotateUnsupportedTypes' }),
              icon: (
                <RotateIcon style={{ marginRight: 5 }} stroke={'#5b5d73'} />
              ),
            });
          }
          break;
        case 'mirror':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_MIRROR,
              label: intl.formatMessage({ id: 'umoGridItem.menuMirror' }),
              disabled: isUnsupportedForEdit,
              tooltipMessage: hasNoDerivativeAccess
                ? intl.formatMessage({ id: 'umoGridItem.noDerivativeAccess' })
                : intl.formatMessage({ id: 'umoGridItem.mirrorUnsupportedTypes' }),
              icon: (
                <FlipHorizontal style={{ marginRight: 5 }} stroke={'#5b5d73'} />
              ),
            });
          }
          break;
        case 'imageAnno':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_IMAGE_ANNOTATE,
              label: intl.formatMessage({
                id: 'umoGridItem.menuImageAnnotator',
              }),
              icon: (
                <PhotographyRequestIcon
                  width="20"
                  height="20"
                  style={{ marginRight: 5 }}
                />
              ),
            });
          }
          break;
        case 'resize':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_RESIZE,
              label: intl.formatMessage({ id: 'umoGridItem.menuResize' }),
              icon: <ResizeIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        case 'audioAnno':
          if (umo.type === UMO_TYPE_AUDIO && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_AUDIO_ANNOTATE,
              label: intl.formatMessage({
                id: 'umoGridItem.menuAudioAnnotator',
              }),
              icon: <RelateIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        case 'videoAnno':
          if (umo.type === UMO_TYPE_VIDEO && isEditMode() && !isRestricted) {
            toolOptions.push({
              key: KEY_VIDEO_ANNOTATE,
              label: intl.formatMessage({
                id: 'umoGridItem.menuVideoAnnotator',
              }),
              icon: <RelateIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        case 'videoSnip':
          if (umo.type === UMO_TYPE_VIDEO && !isRestricted) {
            toolOptions.push({
              key: KEY_TRIMMER,
              label: intl.formatMessage({ id: 'umoGridItem.menuVideoSnippet' }),
              icon: <EditIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        case 'relateUMOs':
          showRelateMenu = true;
          relateOption = (
            <Dropdown.Item
              key={menuOptions.length + 1}
              className={itemClass}
              eventKey={KEY_VIEW_RELATED}
              onClick={onMenuItemClick}
              data-keytype={KEY_VIEW_RELATED}
            >
              <ViewPeersIcon style={{ marginRight: 5 }} />
              {intl.formatMessage({ id: 'umoGridItem.menuViewPeers' })}
            </Dropdown.Item>
          );
          break;
        case 'mediaPopulate':
          if (!isRestricted) {
            toolOptions.push({
              key: KEY_META_POPULATE,
              label: intl.formatMessage({ id: 'umoGridItem.menuMetaPopulate' }),
              icon: <MediaPopulateIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        case 'metaCopy':
          if (!isRestricted) {
            toolOptions.push({
              key: KEY_META_COPY,
              label: intl.formatMessage({ id: 'umoGridItem.menuMetaCopy' }),
              icon: <CopyIcon style={{ marginRight: 5 }} />,
            });
          }
          break;
        default:
          break;
      }
    });
  } else if (isEditMode() && !isUMOMenu) {
    toolOptions.push({
      key: KEY_META_EDIT,
      label: intl.formatMessage({ id: 'umoGridItem.menuEditMeta' }),
    });

    switch (umo.type) {
      case UMO_TYPE_IMAGE:
        if (!isRestricted) {
          toolOptions.push({
            key: KEY_CROP,
            label: intl.formatMessage({ id: 'umoGridItem.menuCrop' }),
          });
          toolOptions.push({
            key: KEY_ROTATE,
            label: intl.formatMessage({ id: 'umoGridItem.menuRotate' }),
          });
          toolOptions.push({
            key: KEY_MIRROR,
            label: intl.formatMessage({ id: 'umoGridItem.menuMirror' }),
          });
          toolOptions.push({
            key: KEY_RESIZE,
            label: intl.formatMessage({ id: 'umoGridItem.menuResize' }),
          });
        }
        break;
      case UMO_TYPE_AUDIO:
        if (!isRestricted) {
          toolOptions.push({
            key: KEY_AUDIO_ANNOTATE,
            label: intl.formatMessage({ id: 'umoGridItem.menuAudioAnnotator' }),
          });
        }
        break;
      case UMO_TYPE_VIDEO:
        if (!isRestricted) {
          toolOptions.push({
            key: KEY_VIDEO_ANNOTATE,
            label: intl.formatMessage({ id: 'umoGridItem.menuVideoAnnotator' }),
          });
          toolOptions.push({
            key: KEY_TRIMMER,
            label: intl.formatMessage({ id: 'umoGridItem.menuVideoSnippet' }),
          });
        }
        break;
      default:
        break;
    }
  }

  const handleMouseLeave = useCallback(() => {
    const timer = setTimeout(() => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setShow(false);
    }, 500);
    timerRef.current = timer;
  }, []);

  return (
    <DropdownButton
      title={
        <span
          className={cn(
            'umo-grid-item__overlay-more-images-icon',
            classModifier
          )}
        >
          <i className="fas fa-ellipsis-h" />
        </span>
      }
      variant="default"
      id="umo-grid-item__overlay-download"
      className="umo-grid-item__o"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setShow((preShow) => !preShow);
      }}
      drop="down"
      show={show}
    >
      <div
        onMouseLeave={handleMouseLeave}
        onMouseEnter={() => {
          clearTimeout(timerRef.current);
        }}
      >
        <UMOGridDropDownSideMenu
          title={
            <div>
              <DownloadIcon /> &nbsp;&nbsp;
              {intl.formatMessage({ id: 'umoGridItem.menuDownloadsHeader' })}
            </div>
          }
          menuOptions={
            <UMODownloadView
              umo={umo}
              isRestricted={isRestricted}
              itemClass={itemClass}
              dispatch={dispatch}
            />
          }
        />
        {showRelateMenu && (
          <UMOGridDropDownSideMenu
            title={
              <div>
                <ImageIcon /> &nbsp;&nbsp;
                {intl.formatMessage({
                  id: 'umoGridItem.menuRelatedImagesHeader',
                })}
              </div>
            }
            menuOptions={relateOption}
          />
        )}
        {toolOptions.length > 0 && (
          <UMOGridDropDownSideMenu
            title={
              <div>
                <ToolsIcon /> &nbsp;&nbsp;
                {intl.formatMessage({
                  id: 'umoGridItem.menuToolsHeader',
                })}
              </div>
            }
            menuOptions={toolOptions.map((data) => {
              const dropdownItem = (
                <Dropdown.Item
                  key={data.key}
                  className={itemClass}
                  eventKey={data.key}
                  data-keytype={data.key}
                  onClick={onMenuItemClick}
                  disabled={data.disabled}
                >
                  {data.icon && data.icon}
                  {data.label}
                </Dropdown.Item>
              );
              return data.disabled ? (
                <OverlayTrigger
                  overlay={
                    <Tooltip
                      id="tooltip-disabled"
                      className="umo-grid-item__overlay-tooltip"
                    >
                      {data.tooltipMessage}
                    </Tooltip>
                  }
                >
                  <div>{dropdownItem}</div>
                </OverlayTrigger>
              ) : (
                dropdownItem
              );
            })}
          />
        )}
      </div>
    </DropdownButton>
  );
}
UMOGridItemOverlayMenu.propTypes = {
  umo: PropTypes.instanceOf(PictionJS.UMO),
  retrieveRelatedUMOs: PropTypes.func,
  dispatch: PropTypes.func,
  classModifier: PropTypes.string,
  isRestricted: PropTypes.bool,
};

export default injectIntl(
  connect(null, mapDispatchToProps)(UMOGridItemOverlayMenu)
);
